import { useSaveBreakLabourMutation } from './api/create-break-labour';
import { useSaveBreakPartsMutation } from './api/create-break-parts';
import { useSaveCheckLabourMutation } from './api/create-check-labour';
import { useSaveCheckPartsMutation } from './api/create-check-parts';
import { useSaveWheelLabourMutation } from './api/create-wheels-labour';
import { useSaveWheelPartsMutation } from './api/create-wheels-parts';
import { useDeleteBreakLabourMutation } from './api/delete-break-labour';
import { useDeleteBreakPartsMutation } from './api/delete-break-parts';
import { useDeleteCheckLabourMutation } from './api/delete-check-labour';
import { useDeleteCheckPartsMutation } from './api/delete-check-parts';
import { useDeleteWheelLabourMutation } from './api/delete-wheels-labour';
import { useDeleteWheelPartsMutation } from './api/delete-wheels-parts';
import { useSaveBreakItemMutation } from './api/save-break';
import { useSaveDefaultItemMutation } from './api/save-default';
import { useSaveWheelItemMutation } from './api/save-wheels';
import { useUpdateBreakItemMutation } from './api/update-break';
import { useUpdateBreakLabourMutation } from './api/update-break-labour';
import { useUpdateBreakPartsMutation } from './api/update-break-parts';
import { useUpdateCheckLabourMutation } from './api/update-check-labour';
import { useUpdateCheckPartsMutation } from './api/update-check-parts';
import { useUpdateDefaultItemMutation } from './api/update-default';
import { useUpdateWheelItemMutation } from './api/update-wheels';
import { useUpdateWheelLabourMutation } from './api/update-wheels-labour';
import { useUpdateWheelPartsMutation } from './api/update-wheels-parts';
import { VhcRecordBreakForm } from './break';
import { VhcRecordDefaultForm } from './default-form';
import { useWaitingStore } from './waiting-context';
import { VhcRecordWheelTyreForm } from './wheelTyre';
import { VHCRecordCheckBrakeItemTypes, VHCRecordCheckWheelTyreItemTypes } from 'api/generated';
import { InlineSvg } from 'components/svg';
import { useGetVhcRecordQuery } from 'features/vhc/api/get-record';
import { VhcConfirmationModal } from 'features/vhc/modals/confirmation';
import { RecordCheckBrakeItem, RecordCheckItem, RecordCheckWheelTyreItem, VhcItem } from 'features/vhc/model';
import { useEffectiveItems } from 'features/vhc/model/useEffectiveItems';
import { useVhcStore } from 'features/vhc/model/vhc-store';
import { useEffect, useMemo, useState } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { FormProvider, UseFormReturn } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

export type FormType = 'OK' | 'NOT_OK';

interface IProps {
  handleClose: () => void;
  formData: UseFormReturn<VhcItem>;
}

export const VhcRecordForm = ({ handleClose, formData }: IProps) => {
  const { isWaiting } = useWaitingStore();
  const { recordModalData, setRecordModalIsOpen } = useVhcStore();

  const { isReport, isComplete } = recordModalData || {};

  const { handleSubmit, watch, getValues, formState, setError, clearErrors } = formData;

  const { isValid } = formState;

  const { mutateAsync: updateDefaultItem, isLoading: isUpdatingDefaultItem } = useUpdateDefaultItemMutation();

  const { mutateAsync: updateBreakItem, isLoading: isUpdatingBreakItem } = useUpdateBreakItemMutation();

  const { mutateAsync: updateWheelItem, isLoading: isUpdatingWheelItem } = useUpdateWheelItemMutation();

  const { id } = useParams();

  const { isLoading: isLoadingVhcRecord } = useGetVhcRecordQuery(
    {
      id: Number(id)
    },
    undefined,
    {
      enabled: false
    }
  );

  const queryClient = useQueryClient();

  const { mutateAsync: createDefaultLabour, isLoading: isCreatingDefaultLabour } = useSaveCheckLabourMutation();
  const { mutateAsync: createBreakLabour, isLoading: isCreatingBreakLabour } = useSaveBreakLabourMutation();
  const { mutateAsync: createWheelLabour, isLoading: isCreatingWheelLabour } = useSaveWheelLabourMutation();

  const { mutateAsync: createDefaultPart, isLoading: isCreatingDefaultPart } = useSaveCheckPartsMutation();
  const { mutateAsync: createBreakPart, isLoading: isCreatingBreakPart } = useSaveBreakPartsMutation();
  const { mutateAsync: createWheelPart, isLoading: isCreatingWheelPart } = useSaveWheelPartsMutation();

  const { mutateAsync: updateDefaultLabour, isLoading: isUpdatingDefaultLabour } = useUpdateCheckLabourMutation();
  const { mutateAsync: updateBreakLabour, isLoading: isUpdatingBreakLabour } = useUpdateBreakLabourMutation();
  const { mutateAsync: updateWheelLabour, isLoading: isUpdatingWheelLabour } = useUpdateWheelLabourMutation();

  const { mutateAsync: updateDefaultPart, isLoading: isUpdatingDefaultPart } = useUpdateCheckPartsMutation();
  const { mutateAsync: updateBreakPart, isLoading: isUpdatingBreakPart } = useUpdateBreakPartsMutation();
  const { mutateAsync: updateWheelPart, isLoading: isUpdatingWheelPart } = useUpdateWheelPartsMutation();

  const { mutateAsync: removeDefaultLabour, isLoading: isRemovingDefaultLabour } = useDeleteCheckLabourMutation();
  const { mutateAsync: removeBreakLabour, isLoading: isRemovingBreakLabour } = useDeleteBreakLabourMutation();
  const { mutateAsync: removeWheelLabour, isLoading: isRemovingWheelLabour } = useDeleteWheelLabourMutation();

  const { mutateAsync: removeDefaultPart, isLoading: isRemovingDefaultPart } = useDeleteCheckPartsMutation();
  const { mutateAsync: removeBreakPart, isLoading: isRemovingBreakPart } = useDeleteBreakPartsMutation();
  const { mutateAsync: removeWheelPart, isLoading: isRemovingWheelPart } = useDeleteWheelPartsMutation();

  const { mutateAsync: createDefaultItem, isLoading: isCreatingDefaultItem } = useSaveDefaultItemMutation();

  const handleSaveDefaultItem = async (data: RecordCheckItem) => {
    if (!data?.id) return;

    const isNew = getValues('isNew');
    let hasError = false;

    if (data.labours?.length) {
      for (let i = 0; i < data.labours.length; i++) {
        const labour = data.labours[i];

        if (!labour.title) {
          hasError = true;

          setError(`labours.${i}.title`, {
            message: 'is required'
          });
        }
      }
    }

    if (data.parts?.length) {
      for (let i = 0; i < data.parts.length; i++) {
        const part = data.parts[i];

        if (!part.description) {
          hasError = true;

          setError(`parts.${i}.description`, {
            message: 'is required'
          });
        }

        if (!part.quantity) {
          hasError = true;

          setError(`parts.${i}.quantity`, {
            message: 'is required'
          });
        }
      }
    }

    if (hasError) {
      throw new Error('Validation Error');
    }

    clearErrors('labours');

    if (isNew) {
      const newData = await createDefaultItem({
        logId: Number(id),
        requestBody: {
          notes: data.notes,
          conditionId: data.conditionId,
          imageUrls: data.imageUrls,
          type: data.type,
          group: data.group,
          description: data.description
        }
      });

      if (data.labours?.length) {
        Promise.all(
          data.labours.map((item) =>
            createDefaultLabour({
              logId: Number(id),
              itemId: newData,
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      if (data.parts?.length) {
        Promise.all(
          data.parts.map((item) => {
            return createDefaultPart({
              logId: Number(id),
              itemId: newData,
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            });
          })
        );
      }
    } else {
      await updateDefaultItem({
        logId: Number(id),
        itemId: data.id,
        requestBody: {
          notes: data.notes,
          conditionId: data.conditionId,
          imageUrls: data.imageUrls,
          type: data.type,
          group: data.group,
          description: data.description
        }
      });

      const labourIds = data.labours?.map((x) => x.id).filter(Boolean) ?? [];
      const deleteLaboursRecord = recordModalData?.data?.labours
        ?.map((x) => x.id)
        .filter((item) => !labourIds.includes(item));

      if (deleteLaboursRecord?.length) {
        await Promise.all(
          deleteLaboursRecord.map((item) => {
            return removeDefaultLabour({
              itemId: recordModalData!.data.id!,
              labourId: item,
              logId: Number(id)
            });
          })
        );
      }

      const updateLabours = data.labours?.filter((x) => x.id);

      if (updateLabours?.length) {
        await Promise.all(
          updateLabours.map((item) =>
            updateDefaultLabour({
              logId: Number(id),
              itemId: recordModalData!.data.id!,
              labourId: Number(item.id),
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      const newLabours = data.labours?.filter((x) => !x.id);

      if (newLabours?.length) {
        await Promise.all(
          newLabours.map((item) =>
            createDefaultLabour({
              logId: Number(id),
              itemId: data.id!,
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      const partsIds = data.parts?.map((x) => x.id).filter(Boolean) ?? [];
      const deletePartsRecord = recordModalData?.data?.parts
        ?.map((x) => x.id)
        .filter((item) => !partsIds.includes(item));

      if (deletePartsRecord?.length) {
        await Promise.all(
          deletePartsRecord.map((item) =>
            removeDefaultPart({
              itemId: recordModalData!.data.id!,
              partId: item,
              logId: Number(id)
            })
          )
        );
      }

      const updateParts = data.parts?.filter((x) => x.id);

      if (updateParts?.length) {
        await Promise.all(
          updateParts.map((item) =>
            updateDefaultPart({
              logId: Number(id),
              itemId: recordModalData!.data.id!,
              partId: Number(item.id),
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            })
          )
        );
      }

      const newParts = data.parts?.filter((x) => !x.id);

      if (newParts?.length) {
        await Promise.all(
          newParts.map((item) =>
            createDefaultPart({
              logId: Number(id),
              itemId: data.id!,
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            })
          )
        );
      }
    }
  };

  const { mutateAsync: createBrakeItem, isLoading: isCreatingBrakeItem } = useSaveBreakItemMutation();

  const handleSaveBrakeItem = async (data: RecordCheckBrakeItem) => {
    if (!data?.id) return;

    const isNew = getValues('isNew');
    let hasError = false;

    clearErrors('brakeCondition');
    clearErrors('brakeWearPercent');

    const { type } = formData.getValues();

    if (
      type === VHCRecordCheckBrakeItemTypes.FrontPadsAndDiscs ||
      type === VHCRecordCheckBrakeItemTypes.RearPadsAndDiscs
    ) {
      if (data.brakeCondition == null || data.brakeCondition < 0) {
        hasError = true;

        setError('brakeCondition', {
          message: 'Is required'
        });
      }

      if (data.brakeWearPercent == null || data.brakeWearPercent < 0) {
        if (!hasError) {
          hasError = true;
        }

        setError('brakeWearPercent', {
          message: 'Is required'
        });
      }

      if (hasError) {
        throw new Error('Required fields are not filled in');
      }
    }

    if (isNew) {
      const newData = await createBrakeItem({
        logId: Number(id),
        requestBody: {
          notes: data.notes,
          brakeWearPercent: (data.brakeWearPercent ?? -1) < 0 ? undefined : data.brakeWearPercent,
          brakeCondition: (data.brakeCondition ?? -1) < 0 ? undefined : data.brakeCondition,
          conditionId: data.conditionId,
          imageUrls: data.imageUrls,
          type: data.type,
          group: data.group,
          description: data.description
        }
      });

      if (data.labours?.length) {
        await Promise.all(
          data.labours.map((item) =>
            createBreakLabour({
              logId: Number(id),
              itemId: newData,
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      if (data.parts?.length) {
        await Promise.all(
          data.parts.map((item) =>
            createBreakPart({
              logId: Number(id),
              itemId: newData,
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            })
          )
        );
      }
    } else {
      await updateBreakItem({
        logId: Number(id),
        itemId: data.id,
        requestBody: {
          notes: data.notes,
          brakeWearPercent: (data.brakeWearPercent ?? -1) < 0 ? undefined : data.brakeWearPercent,
          brakeCondition: (data.brakeCondition ?? -1) < 0 ? undefined : data.brakeCondition,
          conditionId: data.conditionId,
          imageUrls: data.imageUrls,
          type: data.type,
          group: data.group,
          description: data.description
        }
      });

      const labourIds = data.labours?.map((x) => x.id).filter(Boolean) ?? [];
      const deleteLaboursRecord = recordModalData?.data?.labours
        ?.map((x) => x.id)
        .filter((item) => !labourIds.includes(item));

      if (deleteLaboursRecord?.length) {
        await Promise.all(
          deleteLaboursRecord.map((item) =>
            removeBreakLabour({
              itemId: recordModalData!.data.id!,
              labourId: item,
              logId: Number(id)
            })
          )
        );
      }

      const updateLabours = data.labours?.filter((x) => x.id);

      if (updateLabours?.length) {
        await Promise.all(
          updateLabours.map((item) =>
            updateBreakLabour({
              logId: Number(id),
              itemId: recordModalData!.data.id!,
              labourId: Number(item.id),
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      const newLabours = data.labours?.filter((x) => !x.id);

      if (newLabours?.length) {
        await Promise.all(
          newLabours.map((item) =>
            createBreakLabour({
              logId: Number(id),
              itemId: data.id!,
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      const partsIds = data.parts?.map((x) => x.id).filter(Boolean) ?? [];
      const deletePartsRecord = recordModalData?.data?.parts
        ?.map((x) => x.id)
        .filter((item) => !partsIds.includes(item));

      if (deletePartsRecord?.length) {
        await Promise.all(
          deletePartsRecord.map((item) =>
            removeBreakPart({
              itemId: recordModalData!.data.id!,
              partId: item,
              logId: Number(id)
            })
          )
        );
      }

      const updateParts = data.parts?.filter((x) => x.id);

      if (updateParts?.length) {
        await Promise.all(
          updateParts.map((item) =>
            updateBreakPart({
              logId: Number(id),
              itemId: recordModalData!.data.id!,
              partId: Number(item.id),
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            })
          )
        );
      }

      const newParts = data.parts?.filter((x) => !x.id);

      if (newParts?.length) {
        await Promise.all(
          newParts.map((item) =>
            createBreakPart({
              logId: Number(id),
              itemId: data.id!,
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            })
          )
        );
      }
    }
  };

  const { mutateAsync: createWheelItem, isLoading: isCreatingWheelItem } = useSaveWheelItemMutation();

  const handleSaveWheelTyreItem = async (data: RecordCheckWheelTyreItem) => {
    if (!data?.id) return;

    const isNew = getValues('isNew');

    if (isNew) {
      const newData = await createWheelItem({
        logId: Number(id),
        requestBody: {
          conditionId: data.conditionId,
          make: data.make,
          model: data.model,
          size: data.size,
          dotCode: data.dotCode,
          readingInner: data.readingInner == 0 ? undefined : data.readingInner,
          readingMiddle: data.readingMiddle == 0 ? undefined : data.readingMiddle,
          readingOuter: data.readingOuter == 0 ? undefined : data.readingOuter,
          notes: data.notes,
          imageUrls: data.imageUrls,
          type: data.type,
          group: data.group,
          description: data.description
        }
      });

      if (data.labours?.length) {
        await Promise.all(
          data.labours.map((item) =>
            createWheelLabour({
              logId: Number(id),
              itemId: newData,
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      if (data.parts?.length) {
        await Promise.all(
          data.parts.map((item) =>
            createWheelPart({
              logId: Number(id),
              itemId: newData,
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            })
          )
        );
      }
    } else {
      await updateWheelItem({
        logId: Number(id),
        itemId: data.id,
        requestBody: {
          conditionId: data.conditionId,
          make: data.make,
          model: data.model,
          size: data.size,
          dotCode: data.dotCode,
          readingInner: data.readingInner == 0 ? undefined : data.readingInner,
          readingMiddle: data.readingMiddle == 0 ? undefined : data.readingMiddle,
          readingOuter: data.readingOuter == 0 ? undefined : data.readingOuter,
          notes: data.notes,
          imageUrls: data.imageUrls,
          type: data.type,
          group: data.group,
          description: data.description
        }
      });

      const labourIds = data.labours?.map((x) => x.id).filter(Boolean) ?? [];
      const deleteLaboursRecord = recordModalData?.data?.labours
        ?.map((x) => x.id)
        .filter((item) => !labourIds.includes(item));

      if (deleteLaboursRecord?.length) {
        await Promise.all(
          deleteLaboursRecord.map((item) =>
            removeWheelLabour({
              itemId: recordModalData!.data.id!,
              labourId: item,
              logId: Number(id)
            })
          )
        );
      }

      const updateLabours = data.labours?.filter((x) => x.id);

      if (updateLabours?.length) {
        await Promise.all(
          updateLabours.map((item) =>
            updateWheelLabour({
              logId: Number(id),
              itemId: recordModalData!.data.id!,
              labourId: Number(item.id),
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      const newLabours = data.labours?.filter((x) => !x.id);

      if (newLabours?.length) {
        await Promise.all(
          newLabours.map((item) =>
            createWheelLabour({
              logId: Number(id),
              itemId: data.id!,
              requestBody: {
                title: item.title,
                time: item.time
              }
            })
          )
        );
      }

      const partsIds = data.parts?.map((x) => x.id).filter(Boolean) ?? [];
      const deletePartsRecord = recordModalData?.data?.parts
        ?.map((x) => x.id)
        .filter((item) => !partsIds.includes(item));

      if (deletePartsRecord?.length) {
        await Promise.all(
          deletePartsRecord.map((item) =>
            removeWheelPart({
              itemId: recordModalData!.data.id!,
              partId: item,
              logId: Number(id)
            })
          )
        );
      }

      const updateParts = data.parts?.filter((x) => x.id);

      if (updateParts?.length) {
        await Promise.all(
          updateParts.map((item) =>
            updateWheelPart({
              logId: Number(id),
              itemId: recordModalData!.data.id!,
              partId: Number(item.id),
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            })
          )
        );
      }

      const newParts = data.parts?.filter((x) => !x.id);

      if (newParts?.length) {
        await Promise.all(
          newParts.map((item) =>
            createWheelPart({
              logId: Number(id),
              itemId: data.id!,
              requestBody: {
                code: item.code,
                description: item.description,
                quantity: item.quantity
              }
            })
          )
        );
      }
    }
  };

  const handleSaveForm = async (data: VhcItem) => {
    const isNew = getValues('isNew');
    const form = getValues('formType');

    try {
      if (data.parts?.length) {
        let hasError = false;

        for (let i = 0; i < data.parts.length; i++) {
          if (!data.parts[i].description?.trim()) {
            setError(`parts.${i}.description`, {
              message: 'Part description is required'
            });

            hasError = true;
          }
        }

        if (hasError) return;
      }

      if (form === 'default') {
        await handleSaveDefaultItem(data as RecordCheckItem);
      } else if (form === 'brake') {
        await handleSaveBrakeItem(data as RecordCheckBrakeItem);
      } else if (form === 'wheelTyre') {
        await handleSaveWheelTyreItem(data as RecordCheckWheelTyreItem);
      }

      toast.success(isNew ? 'Successfully created!' : 'Successfully updated!');
      await queryClient.invalidateQueries('ArticleByCodeQueryKey');

      if (lastValidIdx != effectiveItems.length - 1) {
        handleNext();
      } else {
        handleClose();
      }
    } catch (error: any) {
      console.error(error);
      toast.error(error.toString());
    }
  };
  const onSubmit = async (data: VhcItem) => {
    await handleSaveForm(data);
  };

  const getVhcRecordForm = () => {
    const form = watch('formType');

    if (form === 'brake') {
      const { type } = formData.getValues();

      if (
        type === VHCRecordCheckBrakeItemTypes.FrontPadsAndDiscs ||
        type === VHCRecordCheckBrakeItemTypes.RearPadsAndDiscs
      ) {
        return <VhcRecordBreakForm />;
      }
      return <VhcRecordDefaultForm />;
    }
    if (form === 'wheelTyre') {
      const { type } = formData.getValues();
      if (
        type === VHCRecordCheckWheelTyreItemTypes.InflationKitOrSpaceSever ||
        type === VHCRecordCheckWheelTyreItemTypes.Misc
      ) {
        return <VhcRecordDefaultForm />;
      }
      return <VhcRecordWheelTyreForm />;
    }
    if (form === 'default') return <VhcRecordDefaultForm />;
    return null;
  };

  const effectiveItems = useEffectiveItems();

  const getCorrectItemIndex = (items: VhcItem[] | null, item?: VhcItem) => {
    if (!items || !item) return -1;
    return items?.findIndex((i) => i.id === item.id && i.type === item.type && i.group === item.group);
  };

  // Находим индекс текущего элемента в отфильтрованном массиве
  const currentIdx = useMemo(
    () => getCorrectItemIndex(effectiveItems, recordModalData?.data),
    [effectiveItems, recordModalData]
  );

  const [lastValidIdx, setLastValidIdx] = useState<number>(-1);

  useEffect(() => {
    if (currentIdx !== -1) {
      setLastValidIdx(currentIdx);
    }
  }, [currentIdx]);

  const handlePrev = () => {
    if (!effectiveItems || effectiveItems.length === 0) return;

    const actualIdx = currentIdx === -1 ? lastValidIdx : currentIdx;

    if (actualIdx <= 0) {
      setRecordModalIsOpen(
        {
          data: effectiveItems[effectiveItems.length - 1],
          isComplete
        },
        true
      );
    } else {
      setRecordModalIsOpen(
        {
          data: effectiveItems[actualIdx - 1],
          isComplete
        },
        true
      );
    }
  };

  const handleNext = () => {
    if (!effectiveItems || effectiveItems.length === 0) return;

    const actualIdx = currentIdx === -1 ? lastValidIdx : currentIdx;

    if (actualIdx === -1 || actualIdx >= effectiveItems.length - 1) {
      setRecordModalIsOpen(
        {
          data: effectiveItems[0],
          isComplete
        },
        true
      );
    } else {
      setRecordModalIsOpen(
        {
          data: effectiveItems[actualIdx + 1],
          isComplete
        },
        true
      );
    }
  };

  const isShowSpinner =
    isUpdatingDefaultItem ||
    isUpdatingBreakItem ||
    isUpdatingWheelItem ||
    isCreatingDefaultLabour ||
    isCreatingBreakLabour ||
    isCreatingWheelLabour ||
    isCreatingDefaultPart ||
    isCreatingBreakPart ||
    isCreatingWheelPart ||
    isUpdatingDefaultLabour ||
    isUpdatingBreakLabour ||
    isUpdatingWheelLabour ||
    isUpdatingDefaultPart ||
    isUpdatingBreakPart ||
    isUpdatingWheelPart ||
    isRemovingDefaultLabour ||
    isRemovingBreakLabour ||
    isRemovingWheelLabour ||
    isRemovingDefaultPart ||
    isRemovingBreakPart ||
    isRemovingWheelPart ||
    isCreatingDefaultItem ||
    isCreatingWheelItem ||
    isCreatingBrakeItem ||
    isLoadingVhcRecord ||
    isWaiting;

  return (
    <>
      <FormProvider {...formData}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Modal.Body className="p-0 pt-2 ">{getVhcRecordForm()}</Modal.Body>
          <Modal.Footer
            className={`d-flex flex-nowrap align-items-center border-0 p-0 ${
              isReport ? 'justify-content-center' : 'justify-content-between'
            }`}
          >
            {!isReport && !isComplete && (
              <button type="button" className="btn btn-icon btn-light-danger me-auto" onClick={handlePrev}>
                <InlineSvg path="/media/icons/duotune/arrows/arr063.svg" className="svg-icon-2" />
              </button>
            )}
            {isShowSpinner ? (
              <Spinner />
            ) : (
              <div className="d-flex gap-3">
                {!isReport && !isComplete && (
                  <button type="submit" className="btn btn-danger" disabled={!isValid}>
                    {lastValidIdx == effectiveItems.length - 1 ? 'Save' : 'Save & Next'}
                  </button>
                )}
                <button type="button" className="btn btn-light-danger" onClick={handleClose}>
                  Close
                </button>
              </div>
            )}
            {!isReport && !isComplete && (
              <button type="button" className="btn btn-icon btn-light-danger ms-auto" onClick={handleNext}>
                <InlineSvg path="/media/icons/duotune/arrows/arr064.svg" className="svg-icon-2" />
              </button>
            )}
          </Modal.Footer>
        </form>
      </FormProvider>
      <VhcConfirmationModal />
    </>
  );
};
